import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Dropdown, Modal, message, Input, Select, Tooltip, Descriptions, Typography } from 'antd';
import {
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ReloadOutlined,
  EyeOutlined,
  SettingOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ConnectorJob, JobStatus, ConnectorType } from '../types/connector';
import { connectorApi } from '../services/connectorApi';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

interface ConnectorDashboardProps {
  onCreateNew: () => void;
  onEdit: (jobId: string) => void;
  onViewDetails: (jobId: string) => void;
  refreshTrigger?: number;
}

const ConnectorDashboard: React.FC<ConnectorDashboardProps> = ({
  onCreateNew,
  onEdit,
  onViewDetails,
  refreshTrigger
}) => {
  const [jobs, setJobs] = useState<ConnectorJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<React.Key[]>([]);
  const [testResult, setTestResult] = useState<any>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: undefined as JobStatus | undefined,
    connectorType: undefined as ConnectorType | undefined
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // Load jobs
  const loadJobs = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const response = await connectorApi.listConnectors({
        page,
        pageSize,
        status: filters.status,
        connectorType: filters.connectorType
      });
      
      setJobs(Array.isArray(response.jobs) ? response.jobs : []);
      setPagination({
        current: page,
        pageSize,
        total: response.total || 0
      });
    } catch (error) {
      message.error('Failed to load connector jobs');
      console.error(error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [filters.status, filters.connectorType, refreshTrigger]);

  // Handle job actions
  const handleStartJob = async (jobId: string) => {
    try {
      await connectorApi.startJob(jobId);
      message.success('Job started successfully');
      loadJobs(pagination.current, pagination.pageSize);
    } catch (error: any) {
      if (error.response?.status === 409) {
        message.error('Job is already running or queued. Try pausing first, then starting again.');
      } else {
        message.error('Failed to start job');
      }
      console.error('Start job error:', error);
    }
  };

  const handlePauseJob = async (jobId: string) => {
    try {
      await connectorApi.pauseJob(jobId);
      message.success('Job paused successfully');
      // Wait a moment then refresh the job list to show updated status
      setTimeout(() => loadJobs(pagination.current, pagination.pageSize), 1000);
    } catch (error) {
      message.error('Failed to pause job');
      console.error('Pause job error:', error);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    console.log('Delete button clicked for job:', jobId);
    
    // Test if message works
    message.info('Delete clicked for ' + jobId);
    
    // Try direct delete without Modal for testing
    const confirmed = window.confirm('Are you sure you want to delete this connector job? This action cannot be undone.');
    if (confirmed) {
      try {
        console.log('Deleting job:', jobId);
        setLoading(true);
        await connectorApi.deleteConnector(jobId);
        message.success('Job deleted successfully');
        await loadJobs(pagination.current, pagination.pageSize);
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to delete job: ' + (error as any)?.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTestConnection = async (jobId: string) => {
    try {
      setLoading(true);
      const result = await connectorApi.testConnection(jobId);
      
      setTestResult(result);
      setShowTestModal(true);
      
      if (result.test_result.success) {
        message.success('Connection test completed - viewing results');
      } else {
        message.error('Connection test failed - viewing results');
      }
    } catch (error) {
      message.error('Connection test failed');
      console.error('Test connection error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: JobStatus }> = ({ status }) => {
    const statusConfig = {
      [JobStatus.PENDING]: { color: 'default', text: 'Pending' },
      [JobStatus.QUEUED]: { color: 'blue', text: 'Queued' },
      [JobStatus.RUNNING]: { color: 'processing', text: 'Running' },
      [JobStatus.COMPLETED]: { color: 'success', text: 'Completed' },
      [JobStatus.FAILED]: { color: 'error', text: 'Failed' },
      [JobStatus.CANCELLED]: { color: 'default', text: 'Cancelled' },
      [JobStatus.PAUSED]: { color: 'warning', text: 'Paused' }
    };

    const config = statusConfig[status];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Actions dropdown for each job
  const getJobActionsItems = (job: ConnectorJob) => [
    {
      key: 'start',
      icon: <PlayCircleOutlined />,
      disabled: [JobStatus.QUEUED, JobStatus.RUNNING].includes(job.status),
      onClick: () => handleStartJob(job.id),
      label: 'Start Job'
    },
    {
      key: 'pause',
      icon: <PauseCircleOutlined />,
      disabled: ![JobStatus.QUEUED, JobStatus.RUNNING].includes(job.status),
      onClick: () => handlePauseJob(job.id),
      label: 'Pause Job'
    },
    {
      key: 'divider-1',
      type: 'divider' as const
    },
    {
      key: 'test',
      icon: <SettingOutlined />,
      onClick: () => handleTestConnection(job.id),
      label: 'Test Connection'
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      onClick: () => onEdit(job.id),
      label: 'Edit Configuration'
    },
    {
      key: 'view',
      icon: <EyeOutlined />,
      onClick: () => onViewDetails(job.id),
      label: 'View Details'
    },
    {
      key: 'divider-2',
      type: 'divider' as const
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      danger: true,
      disabled: [JobStatus.QUEUED, JobStatus.RUNNING].includes(job.status),
      onClick: () => {
        console.log('Delete menu item clicked for job:', job.id, 'status:', job.status);
        handleDeleteJob(job.id);
      },
      label: 'Delete Job'
    }
  ];

  // Table columns
  const columns: ColumnsType<ConnectorJob> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: ConnectorJob, b: ConnectorJob) => a.name.localeCompare(b.name),
      render: (name: string, record: ConnectorJob) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'connector_type',
      key: 'connector_type',
      width: 120,
      filters: [
        { text: 'S3', value: 's3' },
        { text: 'MongoDB', value: 'mongodb' }
      ],
      onFilter: (value: any, record: ConnectorJob) => record.connector_type === value,
      render: (type: ConnectorType) => (
        <Tag color={type === 's3' ? 'blue' : 'green'}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Capability',
      dataIndex: 'capability',
      key: 'capability',
      width: 150,
      render: (capability: string) => {
        const capabilityMap = {
          defense_classification: 'Defense Classification',
          legal_privilege: 'Legal Privilege',
          foia_redaction: 'FOIA Redaction'
        };
        return capabilityMap[capability as keyof typeof capabilityMap] || capability;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: Object.values(JobStatus).map(status => ({
        text: status.charAt(0).toUpperCase() + status.slice(1),
        value: status
      })),
      onFilter: (value: any, record: ConnectorJob) => record.status === value,
      render: (status: JobStatus) => <StatusBadge status={status} />
    },
    {
      title: 'Last Run',
      dataIndex: 'last_run',
      key: 'last_run',
      width: 180,
      sorter: (a: ConnectorJob, b: ConnectorJob) => {
        if (!a.last_run && !b.last_run) return 0;
        if (!a.last_run) return 1;
        if (!b.last_run) return -1;
        return new Date(b.last_run).getTime() - new Date(a.last_run).getTime();
      },
      render: (lastRun: string) => (
        lastRun ? new Date(lastRun).toLocaleString() : 'Never'
      )
    },
    {
      title: 'Next Run',
      dataIndex: 'next_run',
      key: 'next_run',
      width: 180,
      render: (nextRun: string, record: ConnectorJob) => {
        if (!record.schedule) return 'Manual only';
        if (!nextRun) return 'Not scheduled';
        return new Date(nextRun).toLocaleString();
      }
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: ConnectorJob) => (
        <Dropdown menu={{ items: getJobActionsItems(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Data Connectors</h2>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateNew}
            >
              Create Connector
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadJobs(pagination.current, pagination.pageSize)}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <Search
            placeholder="Search connectors..."
            allowClear
            style={{ width: 300 }}
            value={filters.search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onSearch={() => loadJobs(1, pagination.pageSize)}
          />
          
          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: 150 }}
            value={filters.status}
            onChange={(status: JobStatus | undefined) => setFilters(prev => ({ ...prev, status }))}
          >
            {Object.values(JobStatus).map(status => (
              <Option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Filter by Type"
            allowClear
            style={{ width: 150 }}
            value={filters.connectorType}
            onChange={(connectorType: ConnectorType | undefined) => setFilters(prev => ({ ...prev, connectorType }))}
          >
            <Option value="s3">S3</Option>
            <Option value="mongodb">MongoDB</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => 
              `${range[0]}-${range[1]} of ${total} connectors`,
            onChange: (page: number, pageSize: number) => {
              loadJobs(page, pageSize);
            }
          }}
          rowSelection={{
            selectedRowKeys: selectedJobs,
            onChange: setSelectedJobs,
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE
            ]
          }}
          scroll={{ x: 1200 }}
        />

        {/* Bulk Actions */}
        {selectedJobs.length > 0 && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
            <Space>
              <span>{selectedJobs.length} connector(s) selected</span>
              <Button 
                size="small"
                onClick={() => {
                  message.info('Bulk start coming soon');
                }}
              >
                Bulk Start
              </Button>
              <Button 
                size="small"
                onClick={() => {
                  message.info('Bulk pause coming soon');
                }}
              >
                Bulk Pause
              </Button>
              <Button 
                size="small" 
                danger
                onClick={async () => {
                  const confirmed = window.confirm(`Are you sure you want to delete ${selectedJobs.length} connector(s)? This action cannot be undone.`);
                  if (confirmed) {
                    try {
                      setLoading(true);
                      const results = await Promise.allSettled(
                        selectedJobs.map(id => connectorApi.deleteConnector(String(id)))
                      );
                      
                      const succeeded = results.filter(r => r.status === 'fulfilled').length;
                      const failed = results.filter(r => r.status === 'rejected').length;
                      
                      if (succeeded > 0) {
                        message.success(`${succeeded} connector(s) deleted successfully`);
                      }
                      if (failed > 0) {
                        message.error(`${failed} connector(s) failed to delete`);
                      }
                      
                      setSelectedJobs([]);
                      await loadJobs(pagination.current, pagination.pageSize);
                    } catch (error) {
                      message.error('Bulk delete failed: ' + (error as any)?.message);
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
              >
                Bulk Delete
              </Button>
            </Space>
          </div>
        )}
      </Card>
      
      {/* Connection Test Result Modal */}
      <Modal
        title={
          <Space>
            {testResult?.test_result?.success ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            Connection Test Results
          </Space>
        }
        open={showTestModal}
        onCancel={() => setShowTestModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowTestModal(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {testResult && (
          <div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Status">
                <Tag color={testResult.test_result.success ? 'success' : 'error'}>
                  {testResult.test_result.success ? 'SUCCESS' : 'FAILED'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="Message">
                {testResult.test_result.message}
              </Descriptions.Item>
              
              <Descriptions.Item label="Tested At">
                {new Date(testResult.tested_at).toLocaleString()}
              </Descriptions.Item>
              
              {testResult.test_result.details && (
                <Descriptions.Item label="Details">
                  <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '12px' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(testResult.test_result.details, null, 2)}
                    </pre>
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ConnectorDashboard;